import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { LoginField, SuperadminSession } from '@/types/domain'
import type { AuthStatus } from '@/types/ui'

type LoginFields = {
  email: string
  password: string
}

type AuthState = {
  status: AuthStatus
  session: SuperadminSession | null
  loginError: string | null
  isSubmitting: boolean
  fields: LoginFields
}

const initialState: AuthState = {
  status: 'unknown',
  session: null,
  loginError: null,
  isSubmitting: false,
  fields: {
    email: '',
    password: '',
  },
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authBootstrapStarted(state) {
      state.status = 'unknown'
      state.loginError = null
    },
    loginFieldChanged(
      state,
      action: PayloadAction<{ field: LoginField; value: string }>,
    ) {
      state.fields[action.payload.field] = action.payload.value
      state.loginError = null
    },
    loginSubmitted(state) {
      state.isSubmitting = true
      state.loginError = null
    },
    loginSucceeded(state, action: PayloadAction<SuperadminSession>) {
      state.status = 'authenticated'
      state.session = action.payload
      state.isSubmitting = false
      state.loginError = null
      state.fields.password = ''
    },
    loginFailed(state, action: PayloadAction<string>) {
      state.status = 'unauthenticated'
      state.session = null
      state.isSubmitting = false
      state.loginError = action.payload
    },
    sessionRestored(state, action: PayloadAction<SuperadminSession>) {
      state.status = 'authenticated'
      state.session = action.payload
      state.isSubmitting = false
      state.loginError = null
    },
    sessionMissing(state) {
      state.status = 'unauthenticated'
      state.session = null
      state.isSubmitting = false
    },
    logout(state) {
      state.status = 'unauthenticated'
      state.session = null
      state.isSubmitting = false
      state.loginError = null
      state.fields.password = ''
    },
  },
})

export const {
  authBootstrapStarted,
  loginFieldChanged,
  loginSubmitted,
  loginSucceeded,
  loginFailed,
  sessionRestored,
  sessionMissing,
  logout,
} = authSlice.actions

export const authReducer = authSlice.reducer
